import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useAnamAvatar } from '@/hooks/useAnamAvatar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Video, VideoOff, Camera, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CameraCapture, CapturedFrame } from '@/components/simulation/CameraCapture';
import { SessionTimer } from '@/components/simulation/SessionTimer';
import { ResultGenerationDialog } from '@/components/simulation/ResultGenerationDialog';
import { consolidateTranscript } from '@/lib/evaluationCriteria';
import { ANAM_AVATARS, getAvatarByPersonaId } from '@/constants/anamPersonas';
import {
  shouldAutoCompleteCoachingSession,
  withCoachingVoiceContract,
} from '@/lib/coachingRuntime';
import {
  buildCoachingFirstMessage,
  buildCoachingMemoryPrompt,
  fetchCoachingMemoryProfile,
  getLearnerDisplayName,
  type CoachingMemoryProfile
} from '@/lib/coachingMemory';

export default function TemplateCoachingSessionPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { tenant } = useTenant();
  const { toast } = useToast();
  
  const pathwayId = searchParams.get('pathway');
  const moduleId = searchParams.get('module');
  const isDebrief = searchParams.get('debrief') === 'true';
  
  const [template, setTemplate] = useState<any>(null);
  const [coachingMemory, setCoachingMemory] = useState<CoachingMemoryProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<string | null>(null);
  const [cameraInitialized, setCameraInitialized] = useState(false);
  
  // Frame capture state
  const capturedFramesRef = useRef<CapturedFrame[]>([]);
  const [frameCount, setFrameCount] = useState(0);
  
  // Guard against double-clicks
  const isProcessingRef = useRef(false);
  const sessionActiveRef = useRef(false);
  const autoEndSessionRef = useRef<() => void>(() => undefined);
  const autoEndTimerRef = useRef<number | null>(null);

  // Generate stable session ID
  const sessionId = useMemo(() => crypto.randomUUID(), []);

  // Build coaching system prompt from template
  const buildCoachingPrompt = (template: any, memory: CoachingMemoryProfile | null): string => {
    const memoryPrompt = buildCoachingMemoryPrompt(
      memory || {
        learnerName: getLearnerDisplayName(user, profile),
        totalSessionsFound: 0,
        items: []
      }
    );

    const prompt = `You are a professional business coach specialising in ${template.category}.

SESSION OBJECTIVE: ${template.name}
${template.description || ''}
${memoryPrompt}

BEHAVIOR GUIDELINES:
- Stay in character as a professional coach throughout
- Follow the coaching objective above through a natural conversation
- Ask probing follow-up questions based on responses
- Keep responses concise (2-3 sentences typically)
- Show empathy and active listening
- Build rapport and create psychological safety
- Do not break character or mention you are an AI

SESSION ENDING PROTOCOL (CRITICAL):
When the participant indicates they want to end OR when you've gathered sufficient information:
- Provide a brief, warm closing (1-2 sentences maximum)
- Say something like: "Thanks for the conversation. You can review your next steps shortly."
- Do NOT summarise everything discussed in detail
- Do NOT list all the points you collected
- Keep the closing under 15 seconds
- End naturally and stay in character
- Finish the closing with the exact final word: "Goodbye"

The live conversation is complete in itself. Do not discuss any post-session processing.`;

    return withCoachingVoiceContract(prompt);
  };

  // Get the persona ID from template or fall back to first available avatar
  const getPersonaId = () => {
    if (template?.anam_persona_id) {
      return template.anam_persona_id;
    }
    // Fall back to first available Anam avatar
    return ANAM_AVATARS[0].personaId;
  };

  // Get coach display name from template's configured avatar
  const getCoachName = () => {
    if (template?.anam_persona_id) {
      const avatar = getAvatarByPersonaId(template.anam_persona_id);
      return avatar?.name || 'Coach';
    }
    return 'Coach';
  };

  const anam = useAnamAvatar({
    personaId: template ? getPersonaId() : ANAM_AVATARS[0].personaId,
    systemPrompt: template ? buildCoachingPrompt(template, coachingMemory) : '',
    firstMessage: template
      ? buildCoachingFirstMessage({
          configuredFirstMessage: null,
          learnerName: coachingMemory?.learnerName || getLearnerDisplayName(user, profile),
          memory: coachingMemory || {
            learnerName: getLearnerDisplayName(user, profile),
            totalSessionsFound: 0,
            items: []
          },
          sessionName: template.name
        })
      : undefined,
    onTranscriptUpdate: (messages) => {
    },
    onAssistantSpeechEnd: (utterance) => {
      if (
        !sessionActiveRef.current
        || isProcessingRef.current
        || !shouldAutoCompleteCoachingSession(utterance)
      ) return;

      if (autoEndTimerRef.current) window.clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = window.setTimeout(() => {
        autoEndTimerRef.current = null;
        if (sessionActiveRef.current && !isProcessingRef.current) {
          autoEndSessionRef.current();
        }
      }, 1500);
    },
    onSessionEnd: () => {
    },
    onError: (error) => {
      console.error('Anam error:', error);
      toast({
        title: 'Connection Error',
        description: error,
        variant: 'destructive'
      });
    }
  });

  // Frame capture handler
  const handleFrameCaptured = (frame: CapturedFrame) => {
    capturedFramesRef.current.push(frame);
    setFrameCount(capturedFramesRef.current.length);
  };

  const handleCameraError = (error: string) => {
    console.error('Camera error:', error);
  };

  // Fetch template data
  useEffect(() => {
    async function fetchData() {
      if (!templateId || !user?.id) return;

      try {
        const { data: templateData, error: templateError } = await supabase
          .from('coaching_templates')
          .select('*')
          .eq('id', templateId)
          .maybeSingle();

        if (templateError) throw templateError;
        if (!templateData) throw new Error('Coaching template not found');

        const { data: authData } = await supabase.auth.getUser();
        const learnerName = getLearnerDisplayName(user, profile);
        const memory = await fetchCoachingMemoryProfile({
          authUserId: authData.user?.id,
          userId: profile?.id || user.id,
          learnerName,
          currentAgentId: null,
          currentTemplateId: templateData.id
        });

        setCoachingMemory(memory);
        setTemplate(templateData);

      } catch (error: any) {
        console.error('Error fetching template:', error);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive'
        });
        navigate('/learning/pathways');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [templateId, user, profile, navigate, toast]);

  // Initialize Anam when template is ready
  useEffect(() => {
    if (template && !anam.isConnected) {
      anam.initialize();
      setCameraInitialized(true);
    }
  }, [template, coachingMemory]);

  const handleStartSession = async () => {
    try {
      // Set start time FIRST to ensure timer has a valid reference
      const startTime = new Date().toISOString();
      setSessionStartTime(startTime);
      capturedFramesRef.current = [];
      setFrameCount(0);
      
      // Small delay to ensure state is propagated before setting recording
      await new Promise(resolve => setTimeout(resolve, 50));
      setIsRecording(true);

      // Create coaching session record
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) throw new Error('Not authenticated');

      const { error: sessionError } = await supabase
        .from('coaching_sessions')
        .insert({
          session_id: sessionId,
          tenant_id: template.tenant_id ?? tenant.tenant_id,
          agent_id: null, // Template-only sessions don't need an agent
          template_id: template.id,
          user_id: profile!.id,
          auth_user_id: authData.user.id,
          company_id: profile?.company_id || null,
          status: 'in_progress',
          session_start: new Date().toISOString(),
          platform: template.platform || 'anam',
          pathway_module_id: moduleId || null,
          is_debrief: isDebrief
        });

      if (sessionError) throw sessionError;

      // Start Anam streaming
      await anam.startStreaming('coaching-avatar-video');
      sessionActiveRef.current = true;

      toast({
        title: 'Session Started',
        description: `Your ${isDebrief ? 'debrief' : 'coaching'} session has begun.`
      });

    } catch (error: any) {
      console.error('Error starting session:', error);
      toast({
        title: 'Failed to Start',
        description: error.message,
        variant: 'destructive'
      });
      setIsRecording(false);
      sessionActiveRef.current = false;
    }
  };

  // Sample frames evenly
  const sampleFramesEvenly = (frames: CapturedFrame[], maxFrames: number = 30): CapturedFrame[] => {
    if (frames.length <= maxFrames) return frames;
    
    const step = frames.length / maxFrames;
    const sampled: CapturedFrame[] = [];
    
    for (let i = 0; i < maxFrames; i++) {
      const index = Math.floor(i * step);
      sampled.push(frames[index]);
    }
    
    return sampled;
  };

  const handleEndSession = async () => {
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
    setIsProcessing(true);
    sessionActiveRef.current = false;
    anam.interruptPersona();
    if (autoEndTimerRef.current) {
      window.clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
    
    try {
      await anam.stopStreaming();
      setIsRecording(false);

      const rawTranscript = anam.getTranscript();
      const consolidatedTranscript = consolidateTranscript(rawTranscript, 'Coach');
      
      const rawTranscriptArray = rawTranscript.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) throw new Error('Not authenticated');

      // Update session record
      const { error: updateError } = await supabase
        .from('coaching_sessions')
        .update({
          status: 'generating',
          session_end: new Date().toISOString(),
          conversation_transcript: rawTranscriptArray,
          platform: template.platform || 'anam'
        })
        .eq('session_id', sessionId);

      if (updateError) throw updateError;

      toast({
        title: 'Session Completed',
        description: 'Generating your coaching document...'
      });

      // Generate document
      try {
        const { data: docResult, error: docError } = await supabase.functions.invoke(
          'generate-coaching-document',
          { body: { sessionId } }
        );

        if (docError) {
          console.error('Document generation error:', docError);
        }
      } catch (docErr) {
        console.error('⚠️ Document generation error:', docErr);
      }

      // Navigate to results
      let resultsUrl = `/coaching/results/${sessionId}`;
      if (pathwayId && moduleId) {
        resultsUrl += `?pathway=${pathwayId}&module=${moduleId}`;
      }
      
      navigate(resultsUrl);

    } catch (error: any) {
      console.error('Error ending session:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
      setSessionStartTime(null);
    }
  };

  autoEndSessionRef.current = () => {
    void handleEndSession();
  };

  useEffect(() => () => {
    if (autoEndTimerRef.current) {
      window.clearTimeout(autoEndTimerRef.current);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Unable to load coaching session. Template not found.
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ResultGenerationDialog
        open={isProcessing}
        title="Generating your coaching results"
        status={`Saving your session and preparing your ${template.output_document || 'coaching document'}.`}
      />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
              <Video className="w-10 h-10 text-primary" />
            </div>
            {isRecording && (
              <div className="absolute -top-1 -right-1">
                <div className="relative flex h-4 w-4">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-success"></span>
                </div>
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {isDebrief ? 'Debrief Session' : 'Coaching Session'}
          </h1>
          <p className="text-muted-foreground">
            {template.name} • {template.estimated_duration || 30} minutes
          </p>
          {isRecording && (
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-success/10 text-success rounded-full text-sm font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
              </span>
              Live Session
            </div>
          )}
        </div>

        {/* Split Screen Video Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* User Camera */}
          <Card className="overflow-hidden">
            <div className="relative aspect-video bg-muted">
              <CameraCapture
                isRecording={isRecording}
                fps={0.5}
                onFrameCaptured={handleFrameCaptured}
                onError={handleCameraError}
                shouldInitialize={cameraInitialized}
              />
              <div className="absolute bottom-4 left-4 bg-background/80 text-foreground px-3 py-1 rounded text-sm flex items-center gap-2">
                <Camera className="w-4 h-4" />
                You
              </div>
            </div>
          </Card>

          {/* Anam Avatar */}
          <Card className="overflow-hidden">
            <div className="relative aspect-video bg-muted flex items-center justify-center">
              <video
                id="coaching-avatar-video"
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              {!isRecording && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                  <VideoOff className="w-20 h-20 text-muted-foreground" />
                </div>
              )}
              <div className="absolute bottom-4 left-4 bg-background/80 text-foreground px-3 py-1 rounded text-sm flex items-center gap-2">
                <Video className="w-4 h-4" />
                {getCoachName()}
              </div>
            </div>
          </Card>
        </div>

        {/* Session Timer */}
        {isRecording && sessionStartTime && (
          <div className="flex justify-center mb-4">
            <SessionTimer
              maxDurationSeconds={template?.estimated_duration ? template.estimated_duration * 60 : 1200}
              startTime={new Date(sessionStartTime)}
              isActive={isRecording && !!sessionStartTime}
              onTimeExpired={handleEndSession}
              showWarningAt={180}
            />
          </div>
        )}

        {/* Frame count indicator */}
        {isRecording && frameCount > 0 && (
          <div className="text-center mb-4 text-sm text-muted-foreground">
            Capturing visual feedback: {frameCount} frames
          </div>
        )}

        {/* Instructions */}
        {!isRecording && !isProcessing && (
          <Card className="p-6 mb-6 bg-muted/50">
            <h2 className="text-lg font-semibold mb-3">Session Guidelines</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Find a quiet space with good lighting for the best experience</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Your coach will guide you through a structured conversation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>Be open and honest - the more context you provide, the better the output</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>At the end, you'll receive a comprehensive {template.output_document}</span>
              </li>
            </ul>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
          {!isRecording && !isProcessing && (
            <>
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              <Button size="lg" onClick={handleStartSession} className="gap-2">
                <Video className="w-5 h-5" />
                Start Session
              </Button>
            </>
          )}
          
          {isRecording && !isProcessing && (
            <Button
              size="lg"
              variant="destructive"
              onClick={handleEndSession}
              className="gap-2"
            >
              End Session & Generate Document
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
